/**
 * Light Presence Sync Composable
 * Vue composable for syncing light state via Supabase Realtime Presence
 * 
 * Usage in components:
 * const { syncLightState, isReady } = useLightPresenceSync(sceneId)
 * syncLightState('light-123', { brightness: 200, hue: 10000 })
 */

import { ref, watch, onUnmounted, type Ref } from 'vue'
import { lightPresenceService, type LightState, type PresencePayload } from '@/services/realtime/LightPresenceService'
import { useLightsStore } from '@/stores/lights'
import { useAuthStore } from '@/stores/auth'
import { hueBackgroundService } from '@/services/hue/HueBackgroundService'
import { wledBackgroundService } from '@/services/wled/WledBackgroundService'
import { realtimeLogger } from '@/lib/logger'

export function useLightPresenceSync(sceneId: Ref<string | null>) {
  const lightsStore = useLightsStore()
  const authStore = useAuthStore()
  const isReady = ref(false)
  
  let unsubscribeStateChange: (() => void) | null = null

  /**
   * Initialize presence for current scene
   */
  const initialize = async (newSceneId: string | null) => {
    if (!newSceneId) {
      isReady.value = false
      return
    }

    if (!authStore.session?.user?.id) {
      realtimeLogger.warn(`⚠️ [useLightPresenceSync] No user ID available`)
      isReady.value = false
      return
    }

    try {
      await lightPresenceService.initialize(authStore.session.user.id, newSceneId)
      
      // Subscribe to state changes from peers
      unsubscribeStateChange = lightPresenceService.onStateChange(handlePeerStateChange)
      
      isReady.value = true
      realtimeLogger.info(`✅ [useLightPresenceSync] Initialized for scene ${newSceneId}`)
    } catch (error) {
      realtimeLogger.error(`❌ [useLightPresenceSync] Failed to initialize:`, error)
      isReady.value = false
    }
  }

  /**
   * Handle state changes from other clients
   */
  const handlePeerStateChange = (payload: PresencePayload) => {
    realtimeLogger.info(`📨 [useLightPresenceSync] Applying peer state for ${payload.lightId}`)
    
    // Convert presence state to lights store format
    const storeState: Record<string, unknown> = {}
    
    if (payload.state.on !== undefined) {
      storeState.on = payload.state.on
    }
    if (payload.state.brightness !== undefined) {
      storeState.bri = payload.state.brightness
    }
    if (payload.state.hue !== undefined) {
      storeState.hue = payload.state.hue
    }
    if (payload.state.saturation !== undefined) {
      storeState.sat = payload.state.saturation
    }
    if (payload.state.ct !== undefined) {
      storeState.ct = payload.state.ct
    }
    if (payload.state.xy) {
      storeState.xy = payload.state.xy
    }

    // Auto-clear conflicting color mode fields
    if (payload.state.hue !== undefined || payload.state.saturation !== undefined) {
      // Switching to color mode - clear CT
      storeState.ct = undefined
      realtimeLogger.info(`🎨 [useLightPresenceSync] Cleared CT (switching to color mode)`)
    } else if (payload.state.ct !== undefined) {
      // Switching to CT mode - clear color
      storeState.hue = undefined
      storeState.sat = undefined
      storeState.xy = undefined
      realtimeLogger.info(`⚪ [useLightPresenceSync] Cleared hue/sat (switching to CT mode)`)
    }

    // Apply to local store
    lightsStore.updateCurrentState(payload.lightId, storeState)
  }

  /**
   * Broadcast light state change to all connected clients
   */
  const syncLightState = async (lightId: string, stateUpdates: Partial<LightState>) => {
    if (!isReady.value) {
      realtimeLogger.warn(`⚠️ [useLightPresenceSync] Cannot sync - not ready`)
      return
    }

    // First update local state immediately for instant UI feedback
    const storeState: Record<string, unknown> = {}
    if (stateUpdates.on !== undefined) storeState.on = stateUpdates.on
    if (stateUpdates.brightness !== undefined) storeState.bri = stateUpdates.brightness
    if (stateUpdates.hue !== undefined) storeState.hue = stateUpdates.hue
    if (stateUpdates.saturation !== undefined) storeState.sat = stateUpdates.saturation
    if (stateUpdates.ct !== undefined) storeState.ct = stateUpdates.ct
    if (stateUpdates.xy) storeState.xy = stateUpdates.xy

    // Auto-clear conflicting fields
    if (stateUpdates.hue !== undefined || stateUpdates.saturation !== undefined) {
      storeState.ct = undefined
    } else if (stateUpdates.ct !== undefined) {
      storeState.hue = undefined
      storeState.sat = undefined
      storeState.xy = undefined
    }

    lightsStore.updateCurrentState(lightId, storeState)

    // Track this change in background services so they can write to hardware
    hueBackgroundService.trackLocalStateChange(lightId, stateUpdates)
    wledBackgroundService.trackLocalStateChange(lightId, stateUpdates)

    // Then broadcast to other clients
    await lightPresenceService.broadcastLightState(lightId, stateUpdates)
  }

  /**
   * Broadcast multiple light state changes at once
   */
  const syncBatch = async (updates: Array<{ lightId: string; state: Partial<LightState> }>) => {
    if (!isReady.value) {
      realtimeLogger.warn(`⚠️ [useLightPresenceSync] Cannot sync batch - not ready`)
      return
    }

    // Update all local states first
    for (const { lightId, state } of updates) {
      const storeState: any = {}
      if (state.on !== undefined) storeState.on = state.on
      if (state.brightness !== undefined) storeState.bri = state.brightness
      if (state.hue !== undefined) storeState.hue = state.hue
      if (state.saturation !== undefined) storeState.sat = state.saturation
      if (state.ct !== undefined) storeState.ct = state.ct
      if (state.xy) storeState.xy = state.xy

      // Auto-clear conflicting fields
      if (state.hue !== undefined || state.saturation !== undefined) {
        storeState.ct = undefined
      } else if (state.ct !== undefined) {
        storeState.hue = undefined
        storeState.sat = undefined
        storeState.xy = undefined
      }

      lightsStore.updateCurrentState(lightId, storeState)
      
      // Track each change in background services
      hueBackgroundService.trackLocalStateChange(lightId, state)
      wledBackgroundService.trackLocalStateChange(lightId, state)
    }

    // Broadcast batch to other clients
    await lightPresenceService.broadcastBatch(updates)
  }

  /**
   * Get connected peers count
   */
  const getConnectedPeersCount = () => {
    return lightPresenceService.getConnectedPeers().length
  }

  /**
   * Broadcast that groups have changed (splits, merges, deletions)
   */
  const broadcastGroupsChanged = async () => {
    if (!isReady.value) {
      realtimeLogger.warn(`⚠️ [useLightPresenceSync] Cannot broadcast groups - not ready`)
      return
    }

    await lightPresenceService.broadcastGroupsChanged()
  }

  // Watch scene changes and re-initialize
  watch(
    sceneId,
    async (newSceneId) => {
      // Cleanup old subscription
      if (unsubscribeStateChange) {
        unsubscribeStateChange()
        unsubscribeStateChange = null
      }

      await initialize(newSceneId)
    },
    { immediate: true }
  )

  // Cleanup on unmount
  onUnmounted(() => {
    if (unsubscribeStateChange) {
      unsubscribeStateChange()
    }
  })

  return {
    isReady,
    syncLightState,
    syncBatch,
    getConnectedPeersCount,
    broadcastGroupsChanged
  }
}
